import Phaser from 'phaser';
import { FONTS } from '@/ui/theme';
import { AudioManager } from '@/utils/AudioManager';

export interface SubtleLinkOptions {
  x: number;
  y: number;
  label: string;
  onClick: () => void;
  width?: number;
  height?: number;
  fontSize?: number;
}

/**
 * Subtle white-text link with hover bloom + click sting. Identical
 * silhouette to the home-screen SETTINGS label: 200x60 hit area, 24px
 * display type, 0.78 idle alpha, 1.06 hover pop. Carries _haloShape so the
 * shared gamepad-nav halo can ring it as a rounded rect.
 */
export function createSubtleLink(
  scene: Phaser.Scene,
  opts: SubtleLinkOptions,
): Phaser.GameObjects.Container {
  const w = opts.width ?? 200;
  const h = opts.height ?? 60;
  const rr = h / 2;
  const c = scene.add.container(opts.x, opts.y);

  // Cyan selection halo, same family as the menu's focus ring. Hidden by
  // default, fades in on hover / focus so the link has clear feedback.
  const haloGlow = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);
  haloGlow.fillStyle(0x4dd0e1, 0.18);
  haloGlow.fillRoundedRect(-w / 2, -h / 2, w, h, rr);
  haloGlow.fillStyle(0x71D2FF, 0.28);
  haloGlow.fillRoundedRect(-w / 2 + 2, -h / 2 + 2, w - 4, h - 4, Math.max(rr - 2, 1));
  const haloRim = scene.add.graphics().setAlpha(0);
  haloRim.lineStyle(2, 0x88EEFF, 0.6);
  haloRim.strokeRoundedRect(-w / 2, -h / 2, w, h, rr);
  haloRim.lineStyle(1, 0xFFFFFF, 0.45);
  haloRim.strokeRoundedRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, Math.max(rr - 3, 1));
  c.add([haloGlow, haloRim]);

  const t = scene.add.text(0, 0, opts.label, {
    fontFamily: FONTS.display,
    fontSize: `${opts.fontSize ?? 24}px`,
    color: '#FFFFFF',
  }).setOrigin(0.5).setLetterSpacing(2).setAlpha(0.78);
  c.add(t);
  c.setInteractive(
    new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
    Phaser.Geom.Rectangle.Contains,
  );
  (c.input as Phaser.Types.Input.InputConfiguration).cursor = 'pointer';
  c.on('pointerover', () => {
    t.setAlpha(1);
    scene.tweens.add({ targets: c, scale: 1.06, duration: 140, ease: 'Back.easeOut' });
    scene.tweens.add({ targets: [haloGlow, haloRim], alpha: 1, duration: 140, ease: 'Power2' });
  });
  c.on('pointerout', () => {
    t.setAlpha(0.78);
    scene.tweens.add({ targets: c, scale: 1, duration: 120, ease: 'Power2.easeIn' });
    scene.tweens.add({ targets: [haloGlow, haloRim], alpha: 0, duration: 160, ease: 'Power2' });
  });
  c.on('pointerdown', () => {
    AudioManager.getInstance().playSFX(scene,
      scene.cache.audio.exists('sfx_btn_click') ? 'sfx_btn_click' : 'sfx_btn_click_heavy',
      { volume: 0.45 });
    opts.onClick();
  });
  (c as any)._haloShape = { kind: 'rect', w, h };
  return c;
}
